import { Matchup, MatchupTeamEntry, Player, PlayerStats, TeamFixture } from "../models";
import { BullenMatchupResponseEntry } from "../models/api";
import { deepClone } from "./clone";
import { scoreEntry } from "./lineup";
import { decideResult } from "./results";
import { predictMatchup } from "./probability";

const emptyEntry = (name: string, team: string, id: number) => ({
	name,
	team,
	id,
	result: null,
	gamesNotStarted: 0,
	gamesInProgress: 0,
	gamesFinished: 0,
	chip: null,
	totalPoints: 0,
	basePoints: 0,
	currentMinutes: 0,
	totalMinutes: 0,
	players: [],
});

/** Turns the league's head-to-head match list into unscored matchup skeletons. */
export const matchupsFromResponse = (results: BullenMatchupResponseEntry[]): Matchup[] =>
	results.map((m) => ({
		id: m.id,
		home: emptyEntry(m.entry_1_player_name, m.entry_1_name, m.entry_1_entry),
		away: emptyEntry(m.entry_2_player_name, m.entry_2_name, m.entry_2_entry),
	}));

/** The entry ids that need picks fetching for a set of matchups. */
export const entryIdsFor = (matchups: Matchup[]): number[] => matchups.flatMap((m) => [m.home.id, m.away.id]);

/**
 * Scores every matchup. Returns null while any input is still missing so callers can
 * keep showing the previous state.
 */
export const scoreMatchups = (matchups: Matchup[], entries: MatchupTeamEntry[], players: Player[], playerStats: PlayerStats[], fixtureStats: TeamFixture[]): Matchup[] | null => {
	if (players.length === 0 || playerStats.length === 0 || matchups.length === 0 || entries.length === 0 || fixtureStats.length === 0) {
		return null;
	}

	const displayData = deepClone(matchups);
	const scored = entries.map((entry) => scoreEntry(entry, players, playerStats, fixtureStats));

	displayData.forEach((m) => {
		const homeData = scored.find((x) => x.id === m.home.id);
		const awayData = scored.find((x) => x.id === m.away.id);

		if (homeData == null || awayData == null) {
			return;
		}

		const result = decideResult(homeData, awayData);

		m.home = { ...m.home, result: result.home, ...homeData };
		m.away = { ...m.away, result: result.away, ...awayData };
		m.prediction = predictMatchup(homeData, awayData);
	});

	return displayData;
};
